import { useState, useEffect } from 'react';
import { Modal, Button, Form, ListGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import { getBlogFaqs, createFaq, updateFaq, deleteFaq } from './blogUtils';
import './blog.css';

const FaqEditor = ({ show, handleClose, blog }) => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentFaq, setCurrentFaq] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    question: '',
    answer: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch FAQs for the blog when the modal opens
  useEffect(() => {
    if (show && blog) {
      fetchFaqs();
    }
  }, [show, blog]);

  // Reset form when closing the editor mode
  useEffect(() => {
    if (!isEditing) {
      setFormData({
        question: '',
        answer: ''
      });
      setCurrentFaq(null);
    }
  }, [isEditing]);

  const fetchFaqs = async () => {
    if (!blog || !blog.id) return;

    setLoading(true);
    try {
      const response = await getBlogFaqs(blog.id);
      setFaqs(response || []);
    } catch (err) {
      console.error('Error fetching FAQs:', err);
      setError('Failed to load FAQs. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleAddNew = () => {
    setIsEditing(true);
    setCurrentFaq(null);
    setFormData({
      question: '',
      answer: ''
    });
  };

  const handleEdit = (faq) => {
    setIsEditing(true);
    setCurrentFaq(faq);
    setFormData({
      question: faq.question,
      answer: faq.answer
    });
  };

  const handleDelete = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) {
      return;
    }

    setIsSubmitting(true);
    try {
      await deleteFaq(faqId);
      setFaqs(faqs.filter((faq) => faq.id !== faqId));
    } catch (err) {
      console.error('Error deleting FAQ:', err);
      setError('Failed to delete FAQ. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.question || !formData.answer) {
      alert('Please fill in both question and answer fields.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      let response;

      if (currentFaq) {
        // Update existing FAQ
        response = await updateFaq(currentFaq.id, {
          ...formData
          // blog_id: blog.id,
        });

        // Update local state
        setFaqs(faqs.map((faq) => (faq.id === currentFaq.id ? response : faq)));
      } else {
        // Create new FAQ
        response = await createFaq({
          ...formData,
          blog_id: blog.id
        });

        // Update local state
        setFaqs([...faqs, response]);
      }

      // Reset form
      setFormData({
        question: '',
        answer: ''
      });
      setIsEditing(false);
      setCurrentFaq(null);
    } catch (err) {
      console.error('Error saving FAQ:', err);
      setError(err.response?.data?.message || 'Failed to save FAQ. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setCurrentFaq(null);
  };

  return (
    <Modal show={show} onHide={handleClose} className="custom-modal-faq" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{blog ? `Manage FAQs for: ${blog.title}` : 'Manage FAQs'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <div className="alert alert-danger mb-3">{error}</div>}

        {isEditing ? (
          // Edit form
          <Form className="faq-form">
            <Form.Group className="mb-3">
              <Form.Label>Question</Form.Label>
              <Form.Control
                type="text"
                name="question"
                value={formData.question}
                onChange={handleChange}
                placeholder="Enter question"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Answer</Form.Label>
              <Form.Control
                as="textarea"
                rows={5}
                name="answer"
                value={formData.answer}
                onChange={handleChange}
                placeholder="Enter answer"
              />
            </Form.Group>

            <div className="d-flex justify-content-end gap-2 faq-actions">
              <Button variant="secondary" onClick={handleCancel} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {currentFaq ? 'Updating...' : 'Creating...'}
                  </>
                ) : currentFaq ? (
                  'Update FAQ'
                ) : (
                  'Create FAQ'
                )}
              </Button>
            </div>
          </Form>
        ) : (
          // FAQ List
          <>
            <div className="d-flex justify-content-between mb-3">
              <h5>Frequently Asked Questions</h5>
              <Button variant="success" size="sm" onClick={handleAddNew} className="faq-actions">
                <FontAwesomeIcon icon={faPlus} className="me-2" /> Add New FAQ
              </Button>
            </div>

            {loading ? (
              <div className="text-center py-3">Loading FAQs...</div>
            ) : faqs?.length === 0 ? (
              <div className="faq-empty-state">
                <p>No FAQs found for this blog.</p>
                <Button variant="primary" onClick={handleAddNew}>
                  Add Your First FAQ
                </Button>
              </div>
            ) : (
              <ListGroup>
                {faqs.map((faq) => (
                  <ListGroup.Item key={faq.id} className="faq-item">
                    <div className="d-flex justify-content-between">
                      <div>
                        <h6 className="faq-question">{faq.question}</h6>
                        <p className="faq-answer-preview">
                          {faq?.answer?.length > 100 ? `${faq.answer.substring(0, 100)}...` : faq.answer}
                        </p>
                      </div>
                      <div className="d-flex gap-2 faq-actions">
                        <Button variant="outline-primary" size="sm" onClick={() => handleEdit(faq)}>
                          <FontAwesomeIcon icon={faPen} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(faq.id)}
                          disabled={isSubmitting}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FaqEditor;
